import type { Formularz } from '../lib/scalanie';
import type { WspolnaEdycja } from '../lib/wspolna-edycja';
import { Komunikat, Przycisk } from './ui';

function czas(sekundy: number): string {
  if (sekundy < 60) return 'przed chwilą';
  const minuty = Math.round(sekundy / 60);
  return `od ${minuty} min`;
}

/**
 * Wszystko, co formularz ma powiedzieć o pracy kilku osób naraz: kto jeszcze
 * ma ten rekord otwarty, co z cudzych zmian dołączono, i konflikty, w których
 * człowiek musi wybrać wartość. Stoi nad polami formularza.
 */
export function PasekWspolpracy<R, F extends Formularz>({ edycja }: { edycja: WspolnaEdycja<R, F> }) {
  const { inni, dolaczone, konflikty, poScaleniu } = edycja;
  if (inni.length === 0 && !dolaczone && konflikty.length === 0 && !poScaleniu) return null;

  return (
    <div className="flex flex-col gap-3 sm:col-span-2 lg:col-span-3">
      {poScaleniu && <Komunikat rodzaj="sukces">{poScaleniu}</Komunikat>}

      {inni.length > 0 && (
        <Komunikat rodzaj="uwaga" tytul="Ten rekord ma teraz otwarty ktoś jeszcze">
          {inni.map((i) => `${i.imie} (${czas(i.odSekund)})`).join(', ')}.{' '}
          Zmiany w różnych polach połączą się same; jeśli zmienicie to samo pole, zapytamy, której wartości użyć.
        </Komunikat>
      )}

      {dolaczone && (
        <Komunikat rodzaj="info">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p>
              <strong className="font-medium">{dolaczone.kto ?? 'Inna osoba'}</strong> zapisał(a) zmiany w tym
              rekordzie: {dolaczone.pola.join(', ')}. Dołączyliśmy je do formularza — Twoje wpisy zostały.
            </p>
            <Przycisk type="button" wariant="cichy" className="min-h-8 px-2" onClick={edycja.zamknijDolaczone}>
              Rozumiem
            </Przycisk>
          </div>
        </Komunikat>
      )}

      {konflikty.length > 0 && (
        <Komunikat rodzaj="blad" tytul="Obie osoby zmieniły to samo — wybierz, co zostaje">
          <p className="mb-3">
            Zapis jest zablokowany, dopóki nie rozstrzygniesz {konflikty.length === 1 ? 'tego pola' : 'tych pól'}.
          </p>
          <ul className="flex flex-col gap-3">
            {konflikty.map((k) => (
              <li key={k.id} className="rounded-md bg-white px-3 py-3 ring-1 ring-kamien-200">
                <p className="font-medium text-kamien-900">{k.etykieta}</p>
                <dl className="mt-1 grid gap-x-6 gap-y-1 text-kamien-700 sm:grid-cols-2">
                  <div><dt className="text-xs uppercase tracking-wide text-kamien-500">Twoja wartość</dt><dd>{k.opisMoje}</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide text-kamien-500">Wartość drugiej osoby</dt><dd>{k.opisIch}</dd></div>
                </dl>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Przycisk type="button" onClick={() => edycja.rozstrzygnij(k.id, 'moje')}>
                    Zostaw moją
                  </Przycisk>
                  <Przycisk type="button" onClick={() => edycja.rozstrzygnij(k.id, 'ich')}>
                    Weź jej wartość
                  </Przycisk>
                </div>
              </li>
            ))}
          </ul>
        </Komunikat>
      )}
    </div>
  );
}
